import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Megaphone, Save, Send } from "lucide-react";

import { apiErrorOf, tgChannelApi } from "@/api/tgChannel";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/** Вчора за київським часом, YYYY-MM-DD. */
function yesterdayKyiv(): string {
  const d = new Date(Date.now() - 24 * 3600 * 1000);
  return d.toLocaleDateString("sv-SE", { timeZone: "Europe/Kyiv" });
}

/** «Сповіщення» → Telegram-канал для плану й фінзвіту (FR-TGC-01/06). Лише admin. */
export function TgChannelCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["tg-channel"], queryFn: () => tgChannelApi.get() });
  const [chatId, setChatId] = useState("");
  const [reportDate, setReportDate] = useState(yesterdayKyiv());
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    if (q.data) setChatId(q.data.chat_id ?? "");
  }, [q.data]);

  const fail = (e: unknown) => {
    const { code, details } = apiErrorOf(e);
    setStatus({ ok: false, text: t("tg_channel.error", { error: String(details.detail ?? code ?? (e as Error).message) }) });
  };

  const save = useMutation({
    mutationFn: () => tgChannelApi.put(chatId.trim() || null),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tg-channel"] });
      setStatus({ ok: true, text: t("tg_channel.saved") });
    },
    onError: fail,
  });
  const test = useMutation({
    mutationFn: () => tgChannelApi.test(),
    onSuccess: () => setStatus({ ok: true, text: t("tg_channel.test_sent") }),
    onError: fail,
  });
  const report = useMutation({
    mutationFn: () => tgChannelApi.sendFinanceReport(reportDate),
    onSuccess: () => setStatus({ ok: true, text: t("tg_channel.report_sent") }),
    onError: fail,
  });
  const planFact = useMutation({
    mutationFn: () => tgChannelApi.sendPlanFact(reportDate),
    onSuccess: () => setStatus({ ok: true, text: t("tg_channel.plan_fact_sent") }),
    onError: fail,
  });

  const saved = (q.data?.chat_id ?? "") !== "";
  const dirty = chatId.trim() !== (q.data?.chat_id ?? "");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("tg_channel.title")}</CardTitle>
        <CardDescription>{t("tg_channel.hint")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label>{t("tg_channel.chat_id")}</Label>
            <Input
              value={chatId}
              onChange={(e) => setChatId(e.target.value)}
              placeholder="@channel або -100…"
              className="w-[260px]"
            />
          </div>
          <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
            <Save className="h-4 w-4" /> {t("common.save")}
          </Button>
          <Button variant="outline" onClick={() => test.mutate()} disabled={!saved || dirty || test.isPending}>
            <Send className="h-4 w-4" /> {t("tg_channel.test")}
          </Button>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label>{t("tg_channel.report_date")}</Label>
            <Input type="date" value={reportDate} onChange={(e) => setReportDate(e.target.value)} className="w-[180px]" />
          </div>
          <Button variant="outline" onClick={() => report.mutate()} disabled={!saved || dirty || !reportDate || report.isPending}>
            <Megaphone className="h-4 w-4" /> {report.isPending ? t("plan.channel_sending") : t("tg_channel.send_report")}
          </Button>
          <Button variant="outline" onClick={() => planFact.mutate()} disabled={!saved || dirty || !reportDate || planFact.isPending}>
            <Megaphone className="h-4 w-4" /> {planFact.isPending ? t("plan.channel_sending") : t("tg_channel.send_plan_fact")}
          </Button>
        </div>
        {status && <p className={status.ok ? "text-sm text-emerald-700" : "text-sm text-red-600"}>{status.text}</p>}
      </CardContent>
    </Card>
  );
}
